import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchPartnerById } from '@/services/partnerService';
import { fetchProducts } from '@/services/productService';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Partner, Project } from '@/types/partner';
import { Product } from '@/types/product';

const PartnerProjects: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('project');
  const { tl } = useLanguage();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);


  // Reset scroll position when component mounts / project changes
  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentImageIndex(0);
    setSelectedImage(null);
  }, [id, projectIdParam]);

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => fetchPartnerById(id!),
    enabled: !!id,
  });

  const project = useMemo(() => {
    const projects = partner?.projects || [];
    if (!projects.length) return undefined;
    if (projectIdParam) {
      return projects.find((p) => p.id === projectIdParam) || projects[0];
    }
    return projects[0];
  }, [partner?.projects, projectIdParam]);

  // Fetch products used in the selected project
  const { data: projectProducts = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ['projectProducts', project?.id, project?.products],
    queryFn: async () => {
      if (!project?.products?.length) {
        return [];
      }
      const allProducts = await fetchProducts({});
      const projectProductNames = project.products;
      
      // More flexible matching - try exact match first, then case-insensitive
      return allProducts.filter(product =>
        projectProductNames.some(projectProductName => 
          product.name === projectProductName || 
          product.name.toLowerCase() === projectProductName.toLowerCase() ||
          product.name.includes(projectProductName) ||
          projectProductName.includes(product.name)
        )
      );
    },
    enabled: !!project?.products?.length,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-xl text-muted-foreground">
          {tl('Partenaire non trouvé', 'Partner not found', 'Partner non trovato')}
        </p>
      </div>
    );
  }

  const images = project?.images || [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && images.length > 1) {
      nextImage();
    }
    if (isRightSwipe && images.length > 1) {
      prevImage();
    }
  };


  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{project?.title || partner.name} | {tl('Projets', 'Projects', 'Progetti')} | Meubles Karim</title>
      </Helmet>


      {/* Full Screen Hero Section */}
      <motion.div 
        className="relative h-[60vh] sm:h-[70vh] max-h-[800px] w-full overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {(images[0] || partner.logo) ? (
          <motion.img
            src={images[0] || partner.logo}
            alt={project?.title || partner.name}
            className="w-full h-full object-cover object-center"
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-lg">
              {tl('Aucune image disponible', 'No image available', 'Nessuna immagine disponibile')}
            </span>
          </div>
        )}
        <motion.div 
          className="absolute inset-0 bg-black/40 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="text-center"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-sans font-semibold text-white mb-4 sm:mb-6 px-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              {project?.title || partner.name}
            </motion.h1>
            {(project?.location || partner.location) && (
              <motion.p 
                className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-2xl mx-auto px-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              >
                {project?.location || partner.location}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Image Carousel Section */}
      {(images.length > 0 || partner.logo) ? (
        <motion.div 
          className="relative h-[40vh] sm:h-[50vh] md:h-[60vh] max-h-[600px] w-full overflow-hidden mt-10 sm:mt-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 flex items-center">
            {/* Mobile: Show only current image */}
            <div 
              className="block sm:hidden w-full h-full"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {(images[currentImageIndex] || partner.logo) && (
                <img
                  src={images[currentImageIndex] || partner.logo}
                  alt="Current"
                  className="w-full h-full object-cover object-center cursor-pointer"
                  onClick={() => setSelectedImage(images[currentImageIndex] || partner.logo)}
                />
              )}
            </div>

            {/* Desktop: Show carousel with side images */}
            <div className="hidden sm:block w-full h-full">
              {/* Previous Image */}
              <motion.div 
                className="absolute left-0 w-1/4 h-full"
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={images[(currentImageIndex - 1 + images.length) % images.length] || partner.logo}
                  alt="Previous"
                  className="w-full h-full object-cover object-center opacity-50"
                />
              </motion.div>

              {/* Current Image */}
              <motion.div 
                className="absolute left-1/4 w-1/2 h-full"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={images[currentImageIndex] || partner.logo}
                  alt="Current"
                  className="w-full h-full object-cover object-center cursor-pointer"
                  onClick={() => setSelectedImage(images[currentImageIndex] || partner.logo)}
                />
              </motion.div>

              {/* Next Image */}
              <motion.div 
                className="absolute right-0 w-1/4 h-full"
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <img
                  src={images[(currentImageIndex + 1) % images.length] || partner.logo}
                  alt="Next"
                  className="w-full h-full object-cover object-center opacity-50"
                />
              </motion.div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-2 sm:p-3 rounded-full transition-colors touch-manipulation"
            onClick={prevImage}
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </button>
          <button
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-2 sm:p-3 rounded-full transition-colors touch-manipulation"
            onClick={nextImage}
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </button>

          {/* Mobile Image Indicators */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 sm:hidden">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          )}
        </motion.div>
      ) : (
        <div className="mt-10 sm:mt-20 text-center py-20">
          <p className="text-muted-foreground text-lg">
            {tl('Aucune image de projet disponible', 'No project images available', 'Nessuna immagine di progetto disponibile')}
          </p>
          <p className="text-muted-foreground text-sm mt-2">
            {tl("Ce partenaire n'a pas encore de projets associés", "This partner has no associated projects yet", "Questo partner non ha ancora progetti associati")}
          </p>
        </div>
      )}

      {/* Project Information Section */}
      <div className="container mx-auto px-4 py-10 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
          {/* Left Column - Project Description */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl text-black font-sans font-semibold mb-6 sm:mb-8">
              {tl('À propos du projet', 'About the Project', 'Informazioni sul progetto')}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {project?.description}
            </p>
          </motion.div>

          {/* Right Column - Partner Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl text-black font-sans font-semibold mb-4">
                {tl('À propos du partenaire', 'About the Partner', 'Informazioni sul partner')}
              </h2>
              {partner.location && (
                <p className="text-base sm:text-lg text-muted-foreground mb-3">
                  <span className="font-medium">{tl('Localisation:', 'Location:', 'Località:')}</span> {partner.location}
                </p>
              )}
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {partner.description}
              </p>
            </div>

            {partner.website && (
              <Button
                variant="outline"
                className="group relative overflow-hidden border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => window.open(partner.website, '_blank')}
              >
                <span className="relative z-10 flex items-center text-sm sm:text-base">
                  {tl('Voir le site', 'Visit Website', 'Visita il sito')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </span>
                <motion.span 
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </Button>
            )}
          </motion.div>
        </div>

        {/* Products Used in Project - Full Width Section */}
        {productsLoading ? (
          <div className="mt-10 sm:mt-20 text-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">
              {tl('Chargement des produits...', 'Loading products...', 'Caricamento prodotti...')}
            </p>
          </div>
        ) : projectProducts.length > 0 ? (
          <motion.div
            className="mt-10 sm:mt-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl sm:text-3xl text-black font-sans font-semibold mb-6 sm:mb-8 text-center">
              {tl('Produits utilisés', 'Products Used', 'Prodotti utilizzati')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
              {projectProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex flex-col items-center group cursor-pointer transition-transform hover:scale-105 touch-manipulation"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <div className="relative w-full flex justify-center">
                    {product.image_nobg ? (
                      <img
                        src={product.image_nobg}
                        alt={product.name}
                        className="h-48 sm:h-64 object-contain rounded-lg shadow-md"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-48 sm:h-64 w-full flex items-center justify-center bg-gray-100 rounded-lg">
                        <span className="text-gray-500 text-sm">No image</span>
                      </div>
                    )}
                    {product.new && (
                      <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                        {tl('Nouveau', 'New', 'Nuovo')}
                      </span>
                    )}
                    {product.featured && (
                      <span className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                        {tl('Tendance', 'In Trend', 'Di tendenza')}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 sm:mt-4 text-center text-black font-medium text-base sm:text-lg">
                    {product.name}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ) : null}
      </div>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative flex items-center justify-center w-full h-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="relative w-full max-w-4xl max-h-[90vh] sm:max-h-[80vh] bg-white rounded-lg shadow-2xl overflow-hidden">
                <img
                  src={selectedImage}
                  alt="Project"
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/10 hover:bg-white/20 touch-manipulation"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerProjects; 